/**
 * EVENT REMINDER SERVICE
 * 
 * Handles user event reminders and push notifications.
 * Location: src/services/eventReminderService.ts
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import type { CalendarEvent, ReminderTiming, EventReminder } from '../types/event';
import { REMINDER_OPTIONS } from '../types/event';

// ============================================
// REMINDER CRUD
// ============================================

/**
 * Set a reminder for an event
 */
export async function setEventReminder(
  userId: string,
  event: CalendarEvent,
  timing: ReminderTiming
): Promise<string> {
  const reminderId = `${event.id}_${userId}_${timing}`;
  const reminderRef = doc(db, 'eventReminders', reminderId);
  
  const eventDate = event.startDate.toDate();
  const reminderOffset = REMINDER_OPTIONS[timing].milliseconds;
  const reminderDate = new Date(eventDate.getTime() - reminderOffset);
  
  const reminderData: Omit<EventReminder, 'id'> = {
    eventId: event.id!,
    userId,
    timing,
    eventTitle: event.title,
    eventDate,
    brandName: event.brandName,
    reminderDate,
    sent: false,
    createdAt: new Date()
  };
  
  await setDoc(reminderRef, {
    ...reminderData,
    eventDate: Timestamp.fromDate(eventDate),
    reminderDate: Timestamp.fromDate(reminderDate),
    createdAt: serverTimestamp()
  });
  
  return reminderId;
}

/**
 * Remove a reminder for an event
 */
export async function removeEventReminder(
  userId: string,
  eventId: string,
  timing: ReminderTiming
): Promise<void> {
  const reminderId = `${eventId}_${userId}_${timing}`;
  const reminderRef = doc(db, 'eventReminders', reminderId);
  await deleteDoc(reminderRef);
}

/**
 * Remove all reminders for an event (for a user)
 */
export async function removeAllEventReminders(
  userId: string,
  eventId: string
): Promise<void> {
  for (const timing of Object.keys(REMINDER_OPTIONS) as ReminderTiming[]) {
    try {
      await removeEventReminder(userId, eventId, timing);
    } catch (e) {
      // Ignore - reminder might not exist
    }
  }
}

/**
 * Get user's reminders for an event
 */
export async function getEventReminders(
  userId: string,
  eventId: string
): Promise<ReminderTiming[]> {
  const reminders: ReminderTiming[] = [];
  
  for (const timing of Object.keys(REMINDER_OPTIONS) as ReminderTiming[]) {
    const reminderId = `${eventId}_${userId}_${timing}`;
    const reminderRef = doc(db, 'eventReminders', reminderId);
    const reminderSnap = await getDoc(reminderRef);
    
    if (reminderSnap.exists()) {
      reminders.push(timing);
    }
  }
  
  return reminders;
}

/**
 * Get all upcoming reminders for a user
 */
export async function getUserUpcomingReminders(userId: string): Promise<EventReminder[]> {
  const remindersRef = collection(db, 'eventReminders');
  const now = Timestamp.fromDate(new Date());
  
  const q = query(
    remindersRef,
    where('userId', '==', userId),
    where('sent', '==', false),
    where('reminderDate', '>=', now),
    orderBy('reminderDate', 'asc')
  );
  
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    eventDate: doc.data().eventDate.toDate(),
    reminderDate: doc.data().reminderDate.toDate(),
    createdAt: doc.data().createdAt.toDate()
  })) as EventReminder[];
}

// ============================================
// BRAND FOLLOWING FOR EVENT NOTIFICATIONS
// ============================================

/**
 * Subscribe to a brand's events (get notified when they post new events)
 */
export async function subscribeToEventsFromBrand(
  userId: string,
  brandId: string
): Promise<void> {
  const subscriptionRef = doc(db, 'users', userId, 'eventSubscriptions', brandId);
  await setDoc(subscriptionRef, {
    brandId,
    subscribedAt: serverTimestamp()
  });
}

/**
 * Unsubscribe from a brand's events
 */
export async function unsubscribeFromEventsFromBrand(
  userId: string,
  brandId: string
): Promise<void> {
  const subscriptionRef = doc(db, 'users', userId, 'eventSubscriptions', brandId);
  await deleteDoc(subscriptionRef);
}

/**
 * Check if user is subscribed to a brand's events
 */
export async function isSubscribedToEventsFromBrand(
  userId: string,
  brandId: string
): Promise<boolean> {
  const subscriptionRef = doc(db, 'users', userId, 'eventSubscriptions', brandId);
  const subscriptionSnap = await getDoc(subscriptionRef);
  return subscriptionSnap.exists();
}

/**
 * Get all brands a user is subscribed to for events
 */
export async function getEventSubscriptions(userId: string): Promise<string[]> {
  const subscriptionsRef = collection(db, 'users', userId, 'eventSubscriptions');
  const snapshot = await getDocs(subscriptionsRef);
  return snapshot.docs.map(doc => doc.id);
}

// ============================================
// HELPER: FORMAT REMINDER FOR DISPLAY
// ============================================

export function formatReminderTime(timing: ReminderTiming): string {
  return REMINDER_OPTIONS[timing].label;
}
